<script setup lang="ts">
import { computed, ref } from 'vue'
import { Input } from '@/components/ui/input'
import { Search, UserRound, CheckCircle2, PencilLine } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ processes: any[] }>()
const emit = defineEmits<{ (event: 'open', id: number): void }>()
const { t } = useI18n()
const search = ref('')
const categories = ['management', 'core', 'support']

const filtered = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return props.processes
  return props.processes.filter((process) => `${process.code} ${process.title}`.toLowerCase().includes(term))
})

function ownerName(owner: any) {
  return [owner?.firstname, owner?.lastname].filter(Boolean).join(' ') || '—'
}
</script>

<template>
  <div class="space-y-5">
    <div class="relative max-w-xl">
      <Search class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
      <Input v-model="search" class="pl-9" :placeholder="t('processManagement.searchProcesses')" />
    </div>

    <section v-for="category in categories" :key="category" class="overflow-hidden rounded-xl border bg-white shadow-sm">
      <div class="border-b bg-slate-50 px-5 py-3">
        <h2 class="font-semibold text-slate-900">{{ t(`processManagement.categories.${category}`) }}</h2>
      </div>
      <div class="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3">
        <button
          v-for="process in filtered.filter((item) => item.category === category)"
          :key="process.id"
          type="button"
          class="rounded-lg border p-4 text-left transition hover:border-[#80BA27] hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-[#80BA27]/40"
          @click="emit('open', process.id)"
        >
          <div class="flex items-start justify-between gap-3">
            <span class="rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">{{ process.code }}</span>
            <span class="flex items-center gap-1 text-xs" :class="process.unreleased_changes ? 'text-amber-700' : 'text-emerald-700'">
              <PencilLine v-if="process.unreleased_changes" class="h-3.5 w-3.5" />
              <CheckCircle2 v-else class="h-3.5 w-3.5" />
              {{ process.unreleased_changes ? t('processManagement.draft') : t('processManagement.released') }}
            </span>
          </div>
          <h3 class="mt-3 font-medium text-slate-900">{{ process.title }}</h3>
          <p class="mt-3 flex items-center gap-1 text-xs text-slate-500"><UserRound class="h-3.5 w-3.5" />{{ ownerName(process.owner) }}</p>
        </button>
        <p v-if="!filtered.some((item) => item.category === category)" class="col-span-full py-5 text-center text-sm text-slate-400">
          {{ t('processManagement.emptyCategory') }}
        </p>
      </div>
    </section>
  </div>
</template>
